import { Component, type ErrorInfo, type ReactNode } from "react";
import useTranslation from "../hooks/useTranslation";

interface StartupErrorBoundaryProps {
  children: ReactNode;
}

interface StartupErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class StartupErrorBoundary extends Component<
  StartupErrorBoundaryProps,
  StartupErrorBoundaryState
> {
  constructor(props: StartupErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error: Error): Partial<StartupErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    if (__BUILD_VERSION__ === "dev") {
      console.error("[StartupErrorBoundary] Unhandled render error:", error, errorInfo);
    }
  }

  handleRetry(): void {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <StartupErrorScreen
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

function StartupErrorScreen({
  error,
  errorInfo,
  onRetry,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onRetry: () => void;
}) {
  const { translate } = useTranslation();
  const isDev = __BUILD_VERSION__ === "dev";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 px-4">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <div className="rounded-full bg-red-50 p-4">
          <svg
            className="h-8 w-8 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-slate-800">
          {translate("unexpected_error_title")}
        </h2>
        <p className="text-sm leading-relaxed text-slate-500">
          {translate("unexpected_error_body")}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onRetry}
            className="rounded-md bg-slate-800 px-6 py-2.5 text-sm font-medium text-white hover:bg-slate-700 active:bg-slate-900"
          >
            {translate("unexpected_error_retry")}
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border border-slate-300 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100"
          >
            {translate("unexpected_error_reload")}
          </button>
        </div>
        {isDev && error && (
          <details className="mt-2 w-full text-left">
            <summary className="cursor-pointer text-xs text-slate-400">
              {translate("unexpected_error_details")}
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded bg-slate-100 p-3 text-xs text-slate-600 whitespace-pre-wrap">
              {error.stack ?? error.message}
            </pre>
            {errorInfo?.componentStack && (
              <pre className="mt-1 max-h-48 overflow-auto rounded bg-slate-100 p-3 text-xs text-slate-500 whitespace-pre-wrap">
                {errorInfo.componentStack}
              </pre>
            )}
          </details>
        )}
      </div>
    </div>
  );
}
